import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { MAX_AI_INSTRUCTION } from "./builder/sequenceBuilderApi";
import "./builder/gradientAccents.css";

/**
 * Asks the recruiter what the email should say, in their own words, and hands that
 * instruction to the backend's AI drafter.
 *
 * Presentational: the request itself lives in the step editor (which owns the step being
 * written into), so this component only has to collect the instruction and surface whatever
 * came back. `feedback` is the "your instruction wasn't clear enough" path — the modal stays
 * open with the text intact so the recruiter can add to it rather than retype it.
 */
const AIEmailModal = ({
    isOpen,
    onClose,
    onGenerate,
    busy = false,
    feedback = null,
    stepNumber = 1,
    currentSubject = "",
    currentPreview = "",
    hasContent = false,
}) => {
    const [instruction, setInstruction] = useState("");
    const [touched, setTouched] = useState(false);
    const textareaRef = useRef(null);

    // Fresh instruction per opening — a leftover one from the previous step would silently
    // redraft the wrong email.
    useEffect(() => {
        if (isOpen) {
            setInstruction("");
            setTouched(false);
            setTimeout(() => textareaRef.current?.focus(), 50);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const trimmed = instruction.trim();
    const canSubmit = trimmed.length > 0 && !busy;

    const submit = () => {
        setTouched(true);
        if (!canSubmit) return;
        onGenerate(trimmed);
    };

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
        }
    };

    // Closing mid-request would leave a draft landing in a step the recruiter has moved on
    // from, so the exits are simply unavailable while one is in flight.
    const requestClose = () => { if (!busy) onClose(); };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
        >
            <div
                className="w-full max-w-4xl rounded-2xl shadow-2xl bg-bgPrimary dark:bg-[#1B1524] border border-gray-200 dark:border-gray-700/50 overflow-hidden"
                onKeyDown={handleKeyDown}
            >
                {/* Header */}
                <div className="flex items-center gap-2.5 px-6 py-4">
                    <h3 className="text-base font-medium dark:text-white text-textPrimary">Draft this email with AI</h3>
                    <div className="flex-1" />
                    <button
                        onClick={requestClose}
                        disabled={busy}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-6 px-6 pb-2">
                    {/* Left — what's being written into */}
                    <div>
                        <div className="rounded-xl border border-transparent bg-gradient-to-r from-[#FF60B6]/10 via-[#2960FD]/5 to-transparent p-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium dark:text-white text-textPrimary">
                                    Step {stepNumber} • Email
                                </span>
                                <div className="flex-1" />
                                <span className="text-[11px] px-2 py-0.5 rounded-full seq-gradient-fill text-white">
                                    {hasContent ? "Replacing" : "Editing"}
                                </span>
                            </div>
                            <p className="mt-2 text-[13px] dark:text-gray-400 text-textSecondary break-words">
                                {currentSubject?.trim() || currentPreview?.trim() || "(empty)"}
                            </p>
                        </div>
                        {hasContent && (
                            <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                                This step already has content — generating replaces it.
                            </p>
                        )}
                    </div>

                    {/* Right — the instruction */}
                    <div className="flex flex-col">
                        <label className="text-[13px] dark:text-gray-300 text-textPrimary mb-2">
                            What do you want this email to say? <span className="text-red-400">*</span>
                        </label>
                        <textarea
                            ref={textareaRef}
                            value={instruction}
                            onChange={(e) => setInstruction(e.target.value)}
                            disabled={busy}
                            rows={7}
                            maxLength={MAX_AI_INSTRUCTION}
                            placeholder={
                                "Give a brief instruction — e.g. gauge interest in a backend engineering " +
                                "opening, remote-friendly, mention the scale we work at"
                            }
                            className={`w-full resize-none text-sm rounded-xl px-4 py-3 outline-none bg-transparent dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 border transition-colors ${
                                touched && !trimmed
                                    ? "border-red-300 focus:border-red-400"
                                    : "border-gray-200 dark:border-gray-600 focus:border-[#2960FD]"
                            }`}
                        />
                        {/* The role is deliberately not something the recruiter has to know here —
                            say so, since the AI is told the same thing on every request. */}
                        {/* <p className="text-xs mt-2 dark:text-gray-400 text-textSecondary">
                            No need to name the job role — the draft uses{" "}
                            <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-[11px]">
                                {"{{job_role}}"}
                            </code>
                            , filled in when you enroll candidates.
                        </p> */}
                        <div className="flex items-start gap-2 mt-2 min-h-[18px]">
                            {touched && !trimmed && (
                                <p className="text-xs text-red-500 dark:text-red-400">Tell the AI what this email should be about.</p>
                            )}
                            {feedback && (
                                <p className="text-xs text-amber-600 dark:text-amber-400">{feedback}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4">
                    <button
                        onClick={requestClose}
                        disabled={busy}
                        className="text-[13px] dark:text-white text-textSecondary border border-gray-200 dark:border-gray-600 rounded-full px-5 py-2 hover:bg-gray-100 dark:hover:bg-white/10 transition-all disabled:opacity-40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={!canSubmit}
                        className={`button px-6 py-2.5 rounded-full text-white text-[13px] flex items-center gap-2 ${
                            canSubmit ? "" : "opacity-50 cursor-not-allowed"
                        }`}
                    >
                        {busy && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        <span>{busy ? "Writing…" : hasContent ? "Replace email" : "Generate email"}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIEmailModal;
